import React from "react";

/**
 * DualRangeSlider Component
 *
 * A two-thumb range slider for selecting min/max values.
 * Prevents thumbs from crossing each other.
 * Dark mode compatible.
 *
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {Array} value - Current [min, max] values
 * @param {Function} onChange - Callback when values change, receives [min, max]
 * @param {string} label - Label for the slider
 * @param {number} step - Step increment (default: 1)
 * @param {string} unit - Unit to display (e.g., "km")
 *
 * @example
 * <DualRangeSlider
 *   min={0}
 *   max={100}
 *   value={[20, 80]}
 *   onChange={(values) => setRange(values)}
 *   label="Distance Range"
 *   unit="km"
 * />
 */
const DualRangeSlider = ({
  min = 0,
  max = 100,
  value = [min, max],
  onChange,
  label,
  step = 1,
  unit = "",
}) => {
  const [minVal, maxVal] = value;

  // Update min value (ensure it doesn't exceed max)
  const handleMinChange = (e) => {
    const newMin = Math.min(Number(e.target.value), maxVal - step);
    onChange([newMin, maxVal]);
  };

  // Update max value (ensure it doesn't go below min)
  const handleMaxChange = (e) => {
    const newMax = Math.max(Number(e.target.value), minVal + step);
    onChange([minVal, newMax]);
  };

  // Handle text input for min value
  const handleMinInputChange = (e) => {
    const val = e.target.value;
    if (val === "") return; // Allow empty for editing
    const numVal = Number(val);
    if (!isNaN(numVal)) {
      const clampedVal = Math.max(min, Math.min(numVal, maxVal - step));
      onChange([clampedVal, maxVal]);
    }
  };

  // Handle text input for max value
  const handleMaxInputChange = (e) => {
    const val = e.target.value;
    if (val === "") return; // Allow empty for editing
    const numVal = Number(val);
    if (!isNaN(numVal)) {
      const clampedVal = Math.min(max, Math.max(numVal, minVal + step));
      onChange([minVal, clampedVal]);
    }
  };

  // Calculate percentage for visual track
  const minPercent = ((minVal - min) / (max - min)) * 100;
  const maxPercent = ((maxVal - min) / (max - min)) * 100;

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {/* Value Display - Now Editable */}
      <div className="flex items-center justify-between text-sm gap-2">
        <input
          type="number"
          min={min}
          max={maxVal - step}
          step={step}
          value={minVal}
          onChange={handleMinInputChange}
          className="w-20 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-200 font-medium border border-transparent focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all"
        />
        <span className="text-gray-400">—</span>
        <input
          type="number"
          min={minVal + step}
          max={max}
          step={step}
          value={maxVal}
          onChange={handleMaxInputChange}
          className="w-20 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-200 font-medium border border-transparent focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all"
        />
      </div>

      {/* Slider Container */}
      <div className="relative pt-2 pb-4">
        {/* Track Background */}
        <div className="absolute w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full" />

        {/* Active Track */}
        <div
          className="absolute h-2 bg-green-500 dark:bg-green-600 rounded-full"
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
          }}
        />

        {/* Min Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={minVal}
          onChange={handleMinChange}
          className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-green-700 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-green-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:hover:bg-green-700"
          style={{ zIndex: minVal > max - 100 ? 5 : 3 }}
        />

        {/* Max Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxVal}
          onChange={handleMaxChange}
          className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-green-700 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-green-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:hover:bg-green-700"
          style={{ zIndex: 4 }}
        />

        {/* Min/Max Labels */}
        <div className="flex justify-between text-xs text-gray-400 mt-8">
          <span>
            {min}
            {unit}
          </span>
          <span>
            {max}
            {unit}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DualRangeSlider;
